import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    TextField,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import {
    useLazySuggestLocationsQuery,
    useReverseLocationMutation,
} from '../../../services/salonsApi';
import SalonMap from './salonMap';

const REQUIRED_FIELDS = [
    'formatted_address',
    'country_code',
    'admin_area_1',
    'locality',
    'latitude',
    'longitude',
];

const normalizeCandidate = (candidate) => {
    if (!candidate) return null;

    return {
        formatted_address: candidate.formatted_address || '',
        latitude: Number(candidate.latitude),
        longitude: Number(candidate.longitude),
        country_code: candidate.country_code || '',
        country_name: candidate.country_name || '',
        admin_area_1: candidate.admin_area_1 || '',
        admin_area_2: candidate.admin_area_2 || '',
        locality: candidate.locality || '',
        postal_code: candidate.postal_code || '',
        provider: candidate.provider || candidate.geocoding_provider || '',
        provider_place_id: candidate.provider_place_id || '',
        raw: candidate.raw || {},
    };
};

const hasValue = (candidate, field) => {
    const value = candidate?.[field];
    if (field === 'latitude' || field === 'longitude') {
        return Number.isFinite(Number(value));
    }
    return Boolean(String(value || '').trim());
};

const isConfirmedCandidate = (candidate) => Boolean(
    candidate
    && hasValue(candidate, 'formatted_address')
    && hasValue(candidate, 'latitude')
    && hasValue(candidate, 'longitude')
    && (candidate.provider || candidate.geocoding_provider),
);

const SalonAddressPicker = ({
    value,
    onChange,
    disabled = false,
    requireConfirmation = false,
}) => {
    const { t, i18n } = useTranslation();
    const [triggerSuggestions, { isFetching }] = useLazySuggestLocationsQuery();
    const [reverseLocation, { isLoading: isReversing }] = useReverseLocationMutation();
    const [inputValue, setInputValue] = useState(value?.formatted_address || '');
    const [options, setOptions] = useState([]);
    const [confirmed, setConfirmed] = useState(() => isConfirmedCandidate(value));
    const [confirmedCandidate, setConfirmedCandidate] = useState(value || null);
    const [error, setError] = useState('');

    const language = (i18n.language || 'fr').split('-')[0];
    const busy = isFetching || isReversing;

    useEffect(() => {
        setInputValue(value?.formatted_address || '');
        setConfirmed(isConfirmedCandidate(value));
        setConfirmedCandidate(value || null);
    }, [value]);

    useEffect(() => {
        const query = inputValue.trim();
        if (
            disabled
            || confirmed
            || query.length < 2
            || query === value?.formatted_address
        ) {
            if (query.length < 2) {
                setOptions((current) => (current.length > 0 ? [] : current));
            }
            return undefined;
        }

        let active = true;
        const timer = window.setTimeout(async () => {
            try {
                const response = await triggerSuggestions({
                    q: query,
                    language,
                    countryBias: 'dz',
                }).unwrap();
                if (active) {
                    setOptions(response?.results || []);
                    setError('');
                }
            } catch (requestError) {
                if (active) {
                    setOptions([]);
                    setError(t('pro_dashboard.salon.address_search_error'));
                }
            }
        }, 300);

        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [
        confirmed,
        disabled,
        inputValue,
        language,
        t,
        triggerSuggestions,
        value?.formatted_address,
    ]);

    const selectCandidate = useCallback((candidate) => {
        const normalized = normalizeCandidate(candidate);
        if (!normalized) return;

        setInputValue(normalized.formatted_address);
        setOptions([]);
        setConfirmed(true);
        setConfirmedCandidate(normalized);
        setError('');
        onChange(normalized);
    }, [onChange]);

    const reverseCoordinates = useCallback(async ({ latitude, longitude }) => {
        try {
            const response = await reverseLocation({
                latitude: Number(latitude),
                longitude: Number(longitude),
                language,
                force_refresh: false,
            }).unwrap();
            selectCandidate(response.candidate);
        } catch (requestError) {
            setError(t('pro_dashboard.salon.address_search_error'));
        }
    }, [language, reverseLocation, selectCandidate, t]);

    const useCurrentPosition = useCallback(() => {
        if (!navigator.geolocation) {
            setError(t('pro_dashboard.salon.geolocation_not_supported'));
            return;
        }

        setError('');
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => reverseCoordinates({
                latitude: coords.latitude,
                longitude: coords.longitude,
            }),
            () => setError(t('pro_dashboard.salon.geolocation_error')),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
        );
    }, [reverseCoordinates, t]);

    const missingFields = useMemo(() => {
        if (!confirmed || !confirmedCandidate) return [];

        const fields = [...REQUIRED_FIELDS];
        if (String(confirmedCandidate.country_code || '').toUpperCase() === 'DZ') {
            fields.push('postal_code');
        }
        return fields.filter((field) => !hasValue(confirmedCandidate, field));
    }, [confirmed, confirmedCandidate]);

    const showConfirmationWarning = (
        requireConfirmation
        && Boolean(inputValue.trim())
        && !confirmed
    );

    return (
        <Box>
            <Autocomplete
                freeSolo
                filterOptions={(items) => items}
                options={options}
                value={confirmed ? confirmedCandidate : null}
                inputValue={inputValue}
                getOptionLabel={(option) => (
                    typeof option === 'string'
                        ? option
                        : option?.formatted_address || ''
                )}
                isOptionEqualToValue={(option, selected) => (
                    option.provider_place_id === selected.provider_place_id
                    && option.provider === selected.provider
                )}
                loading={busy}
                disabled={disabled}
                onChange={(event, candidate) => {
                    if (candidate && typeof candidate !== 'string') {
                        selectCandidate(candidate);
                    } else if (!candidate) {
                        setConfirmed(false);
                        setConfirmedCandidate(null);
                        onChange(null);
                    }
                }}
                onInputChange={(event, nextInput, reason) => {
                    if (reason !== 'input' && reason !== 'clear') return;
                    setInputValue(nextInput);
                    setConfirmed(false);
                    setConfirmedCandidate(null);
                    setError('');
                    if (value) onChange(null);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('pro_dashboard.salon.complete_address')}
                        placeholder={t('pro_dashboard.salon.search_address_placeholder')}
                        error={showConfirmationWarning}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {busy ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            <Box sx={{ mt: 1, mb: 1 }}>
                <Button
                    type="button"
                    size="small"
                    startIcon={<MyLocationIcon />}
                    onClick={useCurrentPosition}
                    disabled={disabled || busy}
                >
                    {t('pro_dashboard.salon.my_position')}
                </Button>
            </Box>

            {showConfirmationWarning && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {t('pro_dashboard.salon.address_confirmation_required')}
                </Alert>
            )}

            {confirmed && missingFields.length === 0 && (
                <Alert severity="success" sx={{ mb: 1 }}>
                    {t('pro_dashboard.salon.address_complete')}
                </Alert>
            )}

            {confirmed && missingFields.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {t('pro_dashboard.salon.address_missing_fields')}: {missingFields.join(', ')}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {error}
                </Alert>
            )}

            <SalonMap
                coordinates={confirmedCandidate ? {
                    latitude: confirmedCandidate.latitude,
                    longitude: confirmedCandidate.longitude,
                } : null}
                onChange={reverseCoordinates}
                disabled={disabled || busy}
            />
        </Box>
    );
};

export default SalonAddressPicker;
